// components/notifications/NotificationSettings.jsx
import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Volume2, VolumeX, Globe, Moon, Check } from 'lucide-react';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    sound: true,
    desktop: true,
    email: false,
    quietHours: false,
    quietStart: '22:00',
    quietEnd: '08:00'
  });
  
  const [permissionStatus, setPermissionStatus] = useState('default');

  useEffect(() => {
    loadSettings();
    checkPermission();
  }, []);

  const loadSettings = () => {
    const saved = localStorage.getItem('notification_settings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading settings:', e);
      }
    }
  };

  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('notification_settings', JSON.stringify(newSettings));
  };

  const checkPermission = () => {
    if (!('Notification' in window)) {
      setPermissionStatus('unsupported');
      return;
    }
    setPermissionStatus(Notification.permission);
  };

  const requestPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        saveSettings({ ...settings, enabled: true });
        
        // Test notification
        new Notification('✅ Notifications Enabled', {
          body: 'You will now receive notifications',
          icon: '/favicon.ico'
        });
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
    }
  };

  const toggleSetting = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    saveSettings(newSettings);
    
    // Test sound if enabling sound
    if (key === 'sound' && newSettings.sound) {
      testSound();
    }
  };

  const testSound = () => {
    const audio = new Audio('/sounds/notification.mp3');
    audio.volume = 0.3;
    audio.play().catch(e => console.log('Sound test failed:', e));
  };

  const testNotification = () => {
    if (permissionStatus === 'granted') {
      new Notification('🔔 Test Notification', {
        body: 'This is a test notification!',
        icon: '/favicon.ico'
      });
    } else {
      requestPermission();
    }
  };

  if (permissionStatus === 'unsupported') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <BellOff className="w-5 h-5 text-yellow-600" />
          <div>
            <h4 className="font-medium text-yellow-800">Notifications Not Supported</h4>
            <p className="text-sm text-yellow-700">
              Your browser doesn't support desktop notifications
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          {settings.enabled ? (
            <Bell className="w-5 h-5 text-green-500" />
          ) : (
            <BellOff className="w-5 h-5 text-gray-400" />
          )}
          <div>
            <h3 className="font-medium text-gray-900">Push Notifications</h3>
            <p className="text-sm text-gray-600">
              Get real-time alerts on your device
            </p>
          </div>
        </div>
        
        {permissionStatus === 'granted' ? (
          <button
            onClick={() => toggleSetting('enabled')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.enabled ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        ) : (
          <button
            onClick={requestPermission}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600"
          >
            Enable Notifications
          </button>
        )}
      </div>

      {settings.enabled && permissionStatus === 'granted' && (
        <>
          {/* Sound Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              {settings.sound ? (
                <Volume2 className="w-5 h-5 text-gray-600" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-400" />
              )}
              <div>
                <h3 className="font-medium text-gray-900">Notification Sound</h3>
                <p className="text-sm text-gray-600">Play sound for new notifications</p>
              </div>
            </div>
            <button
              onClick={() => toggleSetting('sound')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.sound ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.sound ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Quiet Hours */}
          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Moon className="w-5 h-5 text-gray-600" />
                <div>
                  <h3 className="font-medium text-gray-900">Quiet Hours</h3>
                  <p className="text-sm text-gray-600">Silence notifications during certain hours</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('quietHours')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.quietHours ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.quietHours ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {settings.quietHours && (
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={settings.quietStart}
                    onChange={(e) => saveSettings({ ...settings, quietStart: e.target.value })}
                    className="w-full px-3 py-1.5 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End Time</label>
                  <input
                    type="time"
                    value={settings.quietEnd}
                    onChange={(e) => saveSettings({ ...settings, quietEnd: e.target.value })}
                    className="w-full px-3 py-1.5 border rounded-lg text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Test Button */}
          <button
            onClick={testNotification}
            className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            Test Notification
          </button>
        </>
      )}
    </div>
  );
};

export default NotificationSettings;